import React, { Suspense, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
  useNavigate,
} from "react-router-dom";
import {
  fetchInterviewDetails,
  fetchTryInterviewDetails,
} from "../api/applicantAPI";
import RouteLoading from "../components/CommonComponents/RouteLoading";
import CheckRoute from "./CheckRoute";
import PrivateRoute from "./PrivateRoute";
import CheckAuthAndRedirectJobs from "../utils/CheckAuthAndRedirect";
import RequirementEmailLinkGate from "./RequirementEmailLinkGate";
import LoginLinkExpired from "../components/CommonComponents/LoginLinkExpired";
// Eager-imported: /contact-us is an email-link entry point, so lazy-loading
// only adds a chunk-fetch waterfall on cold boot. Bundling it avoids that.
import Contactus from "../pages/Contactus/Contactus";

// --- Lazy-loaded page components ---
const ApplicantDashboard = React.lazy(
  () => import("../components/ApplicantComponents/ApplicationDashboard"),
);
const MaintainancePage = React.lazy(
  () => import("../components/CommonComponents/MaintainancePage"),
);
const PageNotFound = React.lazy(
  () => import("../components/CommonComponents/PageNotFound"),
);
const Aboutus = React.lazy(() => import("../pages/AboutUs/Aboutus"));
const Checkout = React.lazy(() => import("../pages/Checkout/Checkout"));
const Applicantprofile = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/Applicantprofile/Applicantprofile"),
);
const MyCompany = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/CompanyProfile/CompanyProfile"),
);
// Commented out with its routes — no longer part of the apply flow.
// const ConfirmInformation = React.lazy(
//   () =>
//     import("../pages/Dashboard/ApplicantDashboard/ConfirmInformation/ConfirmInformation"),
// );
const UpdateApplicantResume = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/ConfirmInformation/UpdateApplicantResume"),
);
const BetterLuck = React.lazy(() =>
  import("../pages/Dashboard/ApplicantDashboard/InterviewModule/BetterLuck").then(
    (m) => ({ default: m.BetterLuck }),
  ),
);
const Congratulations = React.lazy(() =>
  import("../pages/Dashboard/ApplicantDashboard/InterviewModule/Congratulations").then(
    (m) => ({ default: m.Congratulations }),
  ),
);
const InterviewAnalyser = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/InterviewModule/InterviewAnalyser"),
);
const InterviewModule = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/InterviewModule/InterviewModule"),
);
const InterviewPanelReady = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/InterviewModule/InterviewPanelReady"),
);
const Interviewpanel = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/InterviewModule/Interviewpanel"),
);
const InteviewReshedule = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/InterviewModule/InteviewReshedule"),
);
const UnderReviewPage = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/InterviewModule/UnderReviewPage"),
);
const RelatedJobs = React.lazy(
  () => import("../pages/Dashboard/ApplicantDashboard/RelatedJobs/RelatedJobs"),
);
const ResumeBuilder = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/ResumeBuilder/ResumeBuilder"),
);
// Commented out with its route — carried the long resume preview and the
// "Enhance Resume with AI" CTA, both removed from the apply flow.
// const ResumeEnhancement = React.lazy(
//   () =>
//     import("../pages/Dashboard/ApplicantDashboard/ResumeBuilder/ResumeEnhancement"),
// );
const ResumeCustomize = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/ResumeCustomize/ResumeCustomize"),
);
const ResumeGenerator = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/ResumeGenerator/ResumeGenerator"),
);
const ResumePicker = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/ResumePicker/ResumePicker"),
);
const CompanyDetails = React.lazy(
  () =>
    import("../pages/Dashboard/RecruiterDashboard/CompanyDetails/CompanyDetails"),
);
const Dashboard = React.lazy(
  () => import("../pages/Dashboard/RecruiterDashboard/Dashboard"),
);
const AiPosting = React.lazy(
  () => import("../pages/Dashboard/RecruiterDashboard/AiPosting/AiPosting"),
);
const JobDetails = React.lazy(
  () => import("../pages/Dashboard/RecruiterDashboard/JobDetails/JobDetails"),
);
const PaymentFailed = React.lazy(
  () =>
    import("../pages/Dashboard/RecruiterDashboard/Monitization/Pages/PaymentFailed/PaymentFailed"),
);
const PaymentSucceded = React.lazy(
  () =>
    import("../pages/Dashboard/RecruiterDashboard/Monitization/Pages/PaymentFailed/paymentSucceded"),
);
const ViewInvitedJobs = React.lazy(
  () => import("../pages/Dashboard/RecruiterDashboard/MyJobs/ViewInvitedJobs"),
);
const ViewJobsData = React.lazy(
  () => import("../pages/Dashboard/RecruiterDashboard/MyJobs/ViewJobsData"),
);
const ViewRequestedJobs = React.lazy(
  () =>
    import("../pages/Dashboard/RecruiterDashboard/MyJobs/ViewRequestedJobs"),
);
const Features = React.lazy(() => import("../pages/Features/Features"));
const Home = React.lazy(() => import("../pages/Home/Home"));
const FortgetPassword = React.lazy(
  () => import("../pages/Login/FortgetPassword"),
);
const LoginEmailPass = React.lazy(
  () => import("../pages/Login/LoginEmailPass"),
);
const LoginVerificationCode = React.lazy(
  () => import("../pages/Login/LoginVerificationCode"),
);
const ResetPassword = React.lazy(() => import("../pages/Login/ResetPassword"));
const Pricing = React.lazy(() => import("../pages/Pricing/Princing"));
const PrivacyPolicy = React.lazy(
  () => import("../pages/PrivacyPolicy/PrivacyPolicy"),
);
const RefundPolicy = React.lazy(
  () => import("../pages/RefundPolicy/RefundPolicy"),
);
const AccountVerification = React.lazy(
  () => import("../pages/SignIn/AccountVerification"),
);
const TermsAndConditon = React.lazy(
  () => import("../pages/TermsCondition/TermAndCondition"),
);
const TryHaire = React.lazy(() => import("../pages/TryHaire/TryHaire"));
const TryHaireAssesment = React.lazy(
  () => import("../pages/TryHaire/TryHaireAssesment"),
);
const TryHaireModals = React.lazy(
  () => import("../pages/TryHaire/TryHaireModal"),
);
const TryInterview = React.lazy(() => import("../pages/TryHaire/TryInterview"));
const ViewDemoJob = React.lazy(
  () => import("../pages/Dashboard/RecruiterDashboard/MyJobs/ViewDemoJob"),
);
const LoginV2 = React.lazy(() => import("../pages/Login/LoginV2"));
const PracticeAssessment = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/InterviewModule/practiceAssessment/PracticeAssessment"),
);
const AiPracticeInterview = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/InterviewModule/AiPracticeInterview"),
);
const AiInterviewGuideLine = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/InterviewModule/AiInterviewGuideLine"),
);
const CodeEditor = React.lazy(() => import("../pages/CodeEditor/CodeEditor"));
const Results = React.lazy(
  () => import("../pages/Dashboard/RecruiterDashboard/Results/Results"),
);
const UserRole = React.lazy(() => import("../pages/Login/UserRole"));
const HomeLanding = React.lazy(() => import("../pages/Home/HomeLanding"));
const WrittenAssessmentInstruction = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/InterviewModule/writtenAssessment/WrittenAssessmentInstruction"),
);
const WrittenAssessment = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/InterviewModule/writtenAssessment/WrittenAssessment"),
);
const InterviewInstruction = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/InterviewModule/InterviewInstruction"),
);
const AiPracticeInterviewGuideLine = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/InterviewModule/AiPracticeInterviewGuideLine"),
);
const Recruiter = React.lazy(() => import("../pages/Recruiter/Recruiter"));
const ClientDashboard = React.lazy(
  () => import("../pages/Dashboard/ClientDashboard/ClientDashboard"),
);
const Analyse = React.lazy(() =>
  import("../pages/Dashboard/ApplicantDashboard/InterviewModule/Analyse").then(
    (m) => ({ default: m.Analyse }),
  ),
);
const Meeting = React.lazy(() => import("../pages/CalendlyMeet/Meeting"));
const TopUpCreditSuccess = React.lazy(
  () =>
    import("../pages/Dashboard/RecruiterDashboard/Monitization/components/TopUpCreditSuccess"),
);
const PaymentSuccededTopup = React.lazy(
  () =>
    import("../pages/Dashboard/RecruiterDashboard/Monitization/Pages/PaymentFailed/paymentSuccededTopup"),
);
const ContactSales = React.lazy(
  () =>
    import("../pages/Dashboard/RecruiterDashboard/Monitization/components/ContactSales"),
);
const MicTroubleshootHelp = React.lazy(
  () =>
    import("../pages/Dashboard/ApplicantDashboard/InterviewModule/MicTroubleshootHelp"),
);
const NewOtpVerificationPage = React.lazy(
  () => import("../pages/Login/NewOtpVerificationPage"),
);
const NewLoginVerificationCode = React.lazy(
  () => import("../pages/Login/NewLoginVerificationCode"),
);
const CookiePolicy = React.lazy(
  () => import("../components/CookiePolicy/CookiePolicy"),
);
const ScheduleConfirmation = React.lazy(() =>
  import("../pages/Dashboard/ApplicantDashboard/ScheduleConfirmation").then(
    (m) => ({ default: m.ScheduleConfirmation }),
  ),
);
const CompanyOnboarding = React.lazy(
  () => import("../pages/UserManagement/onboarding/CompanyOnboarding"),
);
const AgencyOnboarding = React.lazy(
  () => import("../pages/UserManagement/onboarding/AgencyOnboarding"),
);

const LazyFallback = <RouteLoading />;

const ProtectedJobRoute = () => {
  const authToken = useSelector((state) => state.auth.token);
  const location = useLocation();

  if (!authToken) {
    const searchParams = new URLSearchParams(location.search);
    // Read email from raw query so "+" is not decoded as space
    const emailFromQuery = (() => {
      const m = location.search.match(/[?&]email=([^&]*)/);
      return m ? decodeURIComponent(m[1]) : null;
    })();
    const email =
      emailFromQuery && emailFromQuery.trim() ? emailFromQuery.trim() : null;

    if (email) {
      // Redirect to verification-code page with email pre-filled
      const verificationSearch = new URLSearchParams();
      verificationSearch.set("email", email);
      if (
        searchParams.get("from") === "client" ||
        searchParams.get("source") === "client"
      ) {
        verificationSearch.set("from", "client");
      }
      return (
        <Navigate
          to={`/new-login-with-verification-code?${verificationSearch.toString()}`}
          state={{
            redirectTo: location.pathname + location.search,
            from: location,
          }}
          replace
        />
      );
    }

    // Save the attempted URL to redirect back after login
    const isClientLink =
      searchParams.get("from") === "client" ||
      searchParams.get("source") === "client";
    const loginTo = isClientLink
      ? { pathname: "/login", search: "?from=client" }
      : "/login";

    return (
      <Navigate
        to={loginTo}
        state={{
          redirectTo: location.pathname + location.search,
          from: location,
        }}
        replace
      />
    );
  }

  return <ViewJobsData />;
};

const ProtectedInvitedJobRoute = () => {
  const authToken = useSelector((state) => state.auth.token);
  const location = useLocation();

  if (!authToken) {
    const searchParams = new URLSearchParams(location.search);
    // Read email from raw query so "+" is not decoded as space
    const emailFromQuery = (() => {
      const m = location.search.match(/[?&]email=([^&]*)/);
      return m ? decodeURIComponent(m[1]) : null;
    })();
    const email =
      emailFromQuery && emailFromQuery.trim() ? emailFromQuery.trim() : null;

    if (email) {
      // Redirect to verification-code page with email pre-filled
      const verificationSearch = new URLSearchParams();
      verificationSearch.set("email", email);
      if (
        searchParams.get("from") === "client" ||
        searchParams.get("source") === "client"
      ) {
        verificationSearch.set("from", "client");
      }
      return (
        <Navigate
          to={`/new-login-with-verification-code?${verificationSearch.toString()}`}
          state={{
            redirectTo: location.pathname + location.search,
            from: location,
          }}
          replace
        />
      );
    }

    // Save the attempted URL to redirect back after login
    const isClientLink =
      searchParams.get("from") === "client" ||
      searchParams.get("source") === "client";
    const loginTo = isClientLink
      ? { pathname: "/login", search: "?from=client" }
      : "/login";

    return (
      <Navigate
        to={loginTo}
        state={{
          redirectTo: location.pathname + location.search,
          from: location,
        }}
        replace
      />
    );
  }

  return <ViewInvitedJobs />;
};

const ProtectedResultRoute = () => {
  const authToken = useSelector((state) => state.auth.token);
  const location = useLocation();

  if (!authToken) {
    const searchParams = new URLSearchParams(location.search);
    // Read email from raw query so "+" is not decoded as space
    const emailFromQuery = (() => {
      const m = location.search.match(/[?&]email=([^&]*)/);
      return m ? decodeURIComponent(m[1]) : null;
    })();
    const email =
      emailFromQuery && emailFromQuery.trim() ? emailFromQuery.trim() : null;

    if (email) {
      // Redirect to verification-code page with email pre-filled
      const verificationSearch = new URLSearchParams();
      verificationSearch.set("email", email);
      if (
        searchParams.get("from") === "client" ||
        searchParams.get("source") === "client"
      ) {
        verificationSearch.set("from", "client");
      }
      return (
        <Navigate
          to={`/new-login-with-verification-code?${verificationSearch.toString()}`}
          state={{
            redirectTo: location.pathname + location.search,
            from: location,
          }}
          replace
        />
      );
    }

    const isClientLink =
      searchParams.get("from") === "client" ||
      searchParams.get("source") === "client";
    const loginTo = isClientLink
      ? { pathname: "/login", search: "?from=client" }
      : "/login";
    return (
      <Navigate
        to={loginTo}
        state={{
          redirectTo: location.pathname + location.search,
          from: location,
        }}
        replace
      />
    );
  }

  return <Results />;
};

const ProtectedViewJobsDataIndexRoute = () => {
  const authToken = useSelector((state) => state.auth.token);
  const location = useLocation();

  if (!authToken) {
    const searchParams = new URLSearchParams(location.search);
    const isClientLink =
      searchParams.get("from") === "client" ||
      searchParams.get("source") === "client";
    const loginTo = isClientLink
      ? { pathname: "/login", search: "?from=client" }
      : "/login";
    return (
      <Navigate
        to={loginTo}
        state={{
          redirectTo: location.pathname + location.search,
          from: location,
        }}
        replace
      />
    );
  }

  return <ViewJobsData />;
};

const ProtectedDashboardRoute = () => {
  const authToken = useSelector((state) => state.auth.token);
  const location = useLocation();

  if (!authToken) {
    const searchParams = new URLSearchParams(location.search);
    // Read email from raw query so "+" is not decoded as space
    const emailFromQuery = (() => {
      const m = location.search.match(/[?&]email=([^&]*)/);
      return m ? decodeURIComponent(m[1]) : null;
    })();
    const email =
      emailFromQuery && emailFromQuery.trim() ? emailFromQuery.trim() : null;

    if (email) {
      const verificationSearch = new URLSearchParams();
      verificationSearch.set("email", email);
      if (
        searchParams.get("from") === "client" ||
        searchParams.get("source") === "client"
      ) {
        verificationSearch.set("from", "client");
      }
      return (
        <Navigate
          to={`/new-login-with-verification-code?${verificationSearch.toString()}`}
          state={{
            redirectTo: location.pathname + location.search,
            from: location,
          }}
          replace
        />
      );
    }

    const isClientLink =
      searchParams.get("from") === "client" ||
      searchParams.get("source") === "client";
    const loginTo = isClientLink
      ? { pathname: "/login", search: "?from=client" }
      : "/login";

    return (
      <Navigate
        to={loginTo}
        state={{
          redirectTo: location.pathname + location.search,
          from: location,
        }}
        replace
      />
    );
  }

  return <Dashboard />;
};

export const CommonRoutes = () => (
  <Suspense fallback={LazyFallback}>
    <Routes>
      <Route path="/analyser" element={<Analyse />} />
      <Route path="/recruiter" element={<CheckRoute component={Recruiter} />} />
      <Route path="/" element={<CheckRoute component={Home} />} />
      {/* <Route path="/sign-up" element={<CheckRoute component={SignUpPage} />} /> */}
      <Route
        path="/sign-up"
        element={<Navigate to="/user-role" replace={true} />}
      />
      <Route path="/login" element={<CheckRoute component={LoginV2} />} />
      <Route path="/user-role" element={<UserRole />} />
      <Route
        path="/ai-interview"
        element={<CheckRoute component={HomeLanding} />}
      />
      {/* <Route path="/user-role" element={<RecruitingJobSeeking />} /> */}
      <Route
        path="/login-with-phone"
        element={<CheckRoute component={LoginEmailPass} />}
      />
      <Route
        path="/login-with-verification-code"
        element={<CheckRoute component={LoginVerificationCode} />}
      />
      <Route
        path="/new-login-with-verification-code"
        element={<CheckRoute component={NewLoginVerificationCode} />}
      />
      <Route
        path="/otp-verification"
        element={<CheckRoute component={NewOtpVerificationPage} />}
      />
      {/* Keep the login route public */}
      <Route path="/about-us" element={<CheckRoute component={Aboutus} />} />
      <Route
        path="/contact-us"
        element={<CheckRoute component={Contactus} />}
      />
      <Route path="/features" element={<CheckRoute component={Features} />} />
      <Route path="/pricing" element={<CheckRoute component={Pricing} />} />
      <Route path="/Pricing" element={<Navigate to="/pricing" replace />} />
      <Route
        path="/terms-and-conditions"
        element={<CheckRoute component={TermsAndConditon} />}
      />
      <Route
        path="/Term&Condition"
        element={<Navigate to="/terms-and-conditions" replace />}
      />
      <Route
        path="/privacy-policy"
        element={<CheckRoute component={PrivacyPolicy} />}
      />
      <Route
        path="/PrivacyPolicy"
        element={<Navigate to="/privacy-policy" replace />}
      />
      <Route
        path="/cookie-policy"
        element={<CheckRoute component={CookiePolicy} />}
      />
      <Route
        path="/refund-policy"
        element={<CheckRoute component={RefundPolicy} />}
      />
      <Route
        path="/RefundPolicy"
        element={<Navigate to="/refund-policy" replace />}
      />
      <Route path="/forget-password" element={<FortgetPassword />} />
      {/* Keep the forget-password route public */}
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Keep the reset-password route public */}
      <Route path="/jobs" element={<ApplicantDashboard />} />
      <Route path="/try-haire" element={<TryHaire />} />

      {/* <Route path="/try-haire/interview" element={<TryHaireInterview />} /> */}
      <Route path="/jobs/related-jobs/:jobId" element={<RelatedJobs />} />
      <Route path="/resume-builder" element={<ResumeBuilder />} />
      <Route path="/jobs/:jobId" element={<CheckAuthAndRedirectJobs />} />
      <Route path="/link-expired" element={<LoginLinkExpired />} />
      <Route path="/pagenotfound" element={<PageNotFound />} />
      <Route path="/maintainance-page" element={<MaintainancePage />} />
      <Route path="/:companyName/:companyId" element={<MyCompany />} />
      <Route
        path="/dashboard/my-jobs/myJobs/:jobId/view-jobs-data"
        element={<ProtectedJobRoute />}
      />
      <Route
        path="/dashboard/my-jobs/inviteJobs/:jobId/view-invited-jobs-data"
        element={<ProtectedInvitedJobRoute />}
      />
      <Route
        path="/view-jobs-data/results/:activeTab/:id/*"
        element={<ProtectedResultRoute />}
      />
      <Route
        path="/view-jobs-data"
        element={<ProtectedViewJobsDataIndexRoute />}
      />
      <Route
        path="/onboarding/company-details"
        element={<PrivateRoute element={<CompanyOnboarding />} />}
      />
      <Route
        path="/onboarding/agency-details"
        element={<PrivateRoute element={<AgencyOnboarding />} />}
      />
      {/* Intentionally not wrapped in PrivateRoute: anonymous users must hit this route
        so RequirementEmailLinkGate can read ?email=, set redirect, and send them to
        verification/login. PrivateRoute would redirect to generic /login first. */}
      <Route
        path="/myrequirements/:slug/:id"
        element={<RequirementEmailLinkGate />}
      />
      <Route path="/dashboard/*" element={<ProtectedDashboardRoute />} />
      {/* Logged-out users landing on a role dashboard URL (e.g. the brief moment
        right after logout, before signOut()'s hard redirect to "/" completes)
        should fall back to home instead of flashing the 404 page. */}
      <Route
        path="/applicant-dashboard"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/client-dashboard/*"
        element={<Navigate to="/" replace />}
      />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  </Suspense>
);

export const RecruiterRoutes = () => (
  <Suspense fallback={LazyFallback}>
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" />} />
      <Route path="/sign-up" element={<Navigate to="/dashboard" />} />
      <Route path="/login" element={<Navigate to="/dashboard" />} />
      <Route path="/login-with-phone" element={<Navigate to="/dashboard" />} />
      <Route path="/:companyName/:companyId" element={<MyCompany />} />
      <Route path="/contact-sales" element={<ContactSales />}></Route>
      <Route
        path="/try-haire/interview/:interviewId/:jobProfile/:company"
        element={
          <PrivateRoute
            element={<RedirectTryHaire />}
            excludedRoutes={[
              "/try-haire/interview/:interviewId/:jobProfile/:company",
            ]}
          />
        }
      />
      <Route
        path="/try-haire/interview/:interviewId/get-ready/:jobProfile/:company"
        element={
          <PrivateRoute
            element={<TryHaireModals />}
            excludedRoutes={[
              "/try-haire/interview/:interviewId/get-ready/:jobProfile/:company",
            ]}
          />
        }
      />
      <Route
        path="/try-haire/interview/:interviewId/assessment-and-transcript"
        element={
          <PrivateRoute
            element={<TryHaireAssesment />}
            excludedRoutes={[
              "/try-haire/interview/:interviewId/assessment-and-transcript",
            ]}
          />
        }
      />
      <Route
        path="/dashboard/*"
        element={<PrivateRoute element={<Dashboard />} />}
      />
      <Route
        path="/dashboard/ai-posting/:sessionId?"
        element={<PrivateRoute element={<AiPosting />} />}
      />
      <Route
        path="/dashboard/job-details"
        element={<PrivateRoute element={<JobDetails />} />}
      />
      <Route
        path="/view-jobs-data"
        element={<PrivateRoute element={<ViewJobsData />} />}
      />
      <Route
        path="/view-jobs-data/results/:activeTab/:id/*"
        element={<PrivateRoute element={<Results />} />}
      />
      <Route
        path="/view-demo-jobs-data"
        element={<PrivateRoute element={<ViewDemoJob />} />}
      />
      {/* Removed from the apply flow: resume upload now goes straight to the job
          page, which runs the match-score check. resume_json is persisted by
          jobseekers/create/, so there is nothing to confirm. Kept commented
          rather than deleted in case the step needs restoring.
      <Route
        path="/confirm-information"
        element={<PrivateRoute element={<ConfirmInformation />} />}
      />
      */}
      <Route
        path="/account-verification"
        element={<PrivateRoute element={<AccountVerification />} />}
      />
      <Route
        path="/company-details"
        element={<PrivateRoute element={<CompanyDetails />} />}
      />
      <Route
        path="/interview-analyser"
        element={<PrivateRoute element={<InterviewAnalyser />} />}
      />
      <Route
        path="/inteview-reshedule"
        element={<PrivateRoute element={<InteviewReshedule />} />}
      />
      <Route
        path="/check-out"
        element={<PrivateRoute element={<Checkout />} />}
      />
      <Route
        path="/congratulations"
        element={<PrivateRoute element={<Congratulations />} />}
      />
      <Route
        path="/better-luck"
        element={<PrivateRoute element={<BetterLuck />} />}
      />
      <Route
        path="/payment-succeded"
        element={<PrivateRoute element={<PaymentSucceded />} />}
      />
      <Route
        path="/payment-succeded-topup"
        element={<PrivateRoute element={<PaymentSuccededTopup />} />}
      />
      <Route
        path="/payment-failed"
        element={<PrivateRoute element={<PaymentFailed />} />}
      />
      <Route
        path="/view-invited-jobs-data"
        element={<PrivateRoute element={<ViewInvitedJobs />} />}
      />
      <Route
        path="/view-requested-jobs-data"
        element={<PrivateRoute element={<ViewRequestedJobs />} />}
      />
      <Route path="/meeting" element={<PrivateRoute element={<Meeting />} />} />
      <Route
        path="/credit-success"
        element={<PrivateRoute element={<TopUpCreditSuccess />} />}
      />
      <Route path="/link-expired" element={<LoginLinkExpired />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  </Suspense>
);

export const ClientRoutes = () => (
  <Suspense fallback={LazyFallback}>
    <Routes>
      <Route path="/" element={<Navigate to="/client-dashboard" />} />
      <Route path="/sign-up" element={<Navigate to="/client-dashboard" />} />
      <Route path="/login" element={<Navigate to="/client-dashboard" />} />
      <Route
        path="/client-dashboard/*"
        element={<PrivateRoute element={<ClientDashboard />} />}
      />
      <Route
        path="/view-jobs-data/results/:activeTab/:id/*"
        element={<PrivateRoute element={<Results />} />}
      />
      <Route path="/link-expired" element={<LoginLinkExpired />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  </Suspense>
);

export const ApplicantRoutes = () => (
  <Suspense fallback={LazyFallback}>
    <Routes>
      <Route path="/" element={<Navigate to="/applicant-dashboard" />} />
      <Route path="/login" element={<Navigate to="/applicant-dashboard" />} />
      {JobsPageConditionalRedirect()}
      <Route
        path="/login-with-phone"
        element={<Navigate to="/applicant-dashboard" />}
      />
      {/* Public /jobs (footer, header, WhatsApp "Explore Jobs") 404'd when logged in:
          a candidate's job list is the applicant dashboard. Keeps any ?filters. */}
      <Route path="/jobs" element={<JobsToApplicantDashboard />} />
      <Route path="/resume-builder" element={<ResumeBuilder />} />
      <Route path="/jobs/:jobId" element={<CheckAuthAndRedirectJobs />} />
      <Route path="/jobs/related-jobs/:jobId" element={<RelatedJobs />} />
      <Route
        path="/resume-generator"
        element={<PrivateRoute element={<ResumeGenerator />} />}
      />
      <Route
        path="/resume-picker"
        element={<PrivateRoute element={<ResumePicker />} />}
      />
      <Route
        path="/resume-customize"
        element={<PrivateRoute element={<ResumeCustomize />} />}
      />
      <Route
        path="/view-jobs-data"
        element={<PrivateRoute element={<ViewJobsData />} />}
      />
      <Route
        path="/applicantprofile"
        element={<PrivateRoute element={<Applicantprofile />} />}
      />
      <Route
        path="/applicant-dashboard"
        element={<PrivateRoute element={<ApplicantDashboard />} />}
      />
      {/* Removed from the apply flow — see the note on the other
          /confirm-information route above. /resume-enhancement carried the
          long resume preview and the "Enhance Resume with AI" CTA, both of
          which are dropped.
      <Route
        path="/confirm-information"
        element={<PrivateRoute element={<ConfirmInformation />} />}
      />
      <Route
        path="/resume-enhancement"
        element={<PrivateRoute element={<ResumeEnhancement />} />}
      />
      */}
      <Route
        path="/update-applicant-resume"
        element={<PrivateRoute element={<UpdateApplicantResume />} />}
      />
      <Route
        path="/account-verification"
        element={<PrivateRoute element={<AccountVerification />} />}
      />
      <Route
        path="/interview-analyser"
        element={<PrivateRoute element={<InterviewAnalyser />} />}
      />
      <Route
        path="/inteview-reshedule"
        element={<PrivateRoute element={<InteviewReshedule />} />}
      />
      <Route
        path="/check-out"
        element={<PrivateRoute element={<Checkout />} />}
      />
      <Route
        path="/congratulations"
        element={<PrivateRoute element={<Congratulations />} />}
      />
      <Route
        path="/better-luck"
        element={<PrivateRoute element={<BetterLuck />} />}
      />
      <Route
        path="/applicant-dashboard/related-jobs/:jobId"
        element={<PrivateRoute element={<RelatedJobs />} />}
      />
      <Route
        path="/applicant-dashboard/related-jobs/:jobId/interview-module/:jobname/:companyName/:assignment_id"
        element={<PrivateRoute element={<InterviewModule />} />}
      />
      <Route
        path="/applicant-dashboard/related-jobs/:jobId/start-interview/:interviewId/:jobname/:companyName"
        element={<PrivateRoute element={<Interviewpanel />} />}
      />
      <Route
        path="/applicant-dashboard/related-jobs/:jobId/start-interview/:interviewId/interview-panel/interview-panel-ready/:jobname/:companyName"
        element={<PrivateRoute element={<RedirectInterviewPanelReady />} />}
      />
      <Route
        path="/applicant-dashboard/related-jobs/:jobId/start-interview/:interviewId/interview-panel/interview-panel-ready/:jobname/:companyName/practice-interview"
        element={
          <PrivateRoute
            element={
              <InterviewPanelReady
                interviewStatusDetails={{
                  status: 200,
                  signal: true,
                  greeting: true,
                }}
              />
            }
          />
        }
      />
      <Route
        path="/applicant-dashboard/related-jobs/:jobId/practice-assessment/:jobname/:companyName/:assignment_id"
        element={<PrivateRoute element={<PracticeAssessment />} />}
      />
      <Route
        path="/applicant-dashboard/related-jobs/:jobId/written-assessment-instruction/:jobname/:companyName/:assignment_id"
        element={<PrivateRoute element={<WrittenAssessmentInstruction />} />}
      />
      <Route
        path="/applicant-dashboard/related-jobs/:jobId/interview-instruction/:jobname/:companyName"
        element={<PrivateRoute element={<InterviewInstruction />} />}
      />
      <Route
        path="/applicant-dashboard/related-jobs/:jobId/written-assessment-started/:jobname/:companyName/:assignment_id"
        element={<PrivateRoute element={<WrittenAssessment />} />}
      />
      <Route
        path="/applicant-dashboard/related-jobs/:jobId/start-interview/:interviewId/ai-practice-interview-tour/:jobname/:companyName/:assignment_id"
        element={<PrivateRoute element={<AiPracticeInterview />} />}
      />
      <Route
        path="/applicant-dashboard/related-jobs/:jobId/start-interview/:interviewId/ai-interview-tour/:jobname/:companyName"
        element={<PrivateRoute element={<AiPracticeInterview />} />}
      />
      <Route
        path="/applicant-dashboard/related-jobs/:jobId/start-interview/:interviewId/ai-interview-guideline/:jobname/:companyName"
        element={<PrivateRoute element={<AiInterviewGuideLine />} />}
      />
      <Route
        path="/applicant-dashboard/related-jobs/:jobId/start-interview/:interviewId/ai-practice-interview-guideline/:jobname/:companyName/:assignment_id"
        element={<PrivateRoute element={<AiPracticeInterviewGuideLine />} />}
      />
      <Route
        path="/applicant-dashboard/related-jobs/:jobId/under-review/:interviewId/:jobname/:companyName"
        element={<PrivateRoute element={<UnderReviewPage />} />}
      />
      <Route
        path="/help/troubleshoot-microphone"
        element={<MicTroubleshootHelp />}
      />
      <Route
        path="/applicant-dashboard/schedule-confirmation"
        element={<ScheduleConfirmation />}
      />
      <Route path="/:companyName/:companyId" element={<MyCompany />} />
      <Route path="/code-editor" element={<CodeEditor />} />
      <Route path="/link-expired" element={<LoginLinkExpired />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  </Suspense>
);

function JobsToApplicantDashboard() {
  const { search } = useLocation();
  return <Navigate to={`/applicant-dashboard${search}`} replace />;
}

function JobsPageConditionalRedirect() {
  const redirectUrl = useSelector((state) => state.auth?.redirectUrl);

  if (redirectUrl) {
    return <Route path="/sign-up" element={<Navigate to={redirectUrl} />} />;
  }

  return (
    <Route path="/sign-up" element={<Navigate to="/applicant-dashboard" />} />
  );
}

const RedirectInterviewPanelReady = () => {
  const { interviewId, jobId, jobname, companyName } = useParams();
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const [interviewDetails, setInterviewDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async function () {
      setIsLoading(true);
      try {
        const data = await fetchInterviewDetails(token, interviewId);
        if (data?.status === 200) {
          if (data?.result || data?.selection_status) {
            navigate(
              `/applicant-dashboard/related-jobs/${jobId}/under-review/${interviewId}/${jobname}/${companyName}`,
              { replace: true },
            );
          } else {
            setInterviewDetails(data);
          }
        } else {
          navigate(
            `/applicant-dashboard/related-jobs/${jobId}/under-review/${interviewId}/${jobname}/${companyName}`,
            { replace: true },
          );
        }
      } catch (error) {
        navigate(
          `/applicant-dashboard/related-jobs/${jobId}/under-review/${interviewId}/${jobname}/${companyName}`,
          { replace: true },
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [interviewId, token, jobId, jobname, companyName, navigate]);

  if (isLoading || interviewDetails === null) {
    return <RouteLoading />;
  }

  if (
    interviewDetails?.signal === true &&
    !interviewDetails?.result &&
    !interviewDetails?.selection_status
  ) {
    return <InterviewPanelReady interviewStatusDetails={interviewDetails} />;
  }

  navigate(
    `/applicant-dashboard/related-jobs/${jobId}/under-review/${interviewId}/${jobname}/${companyName}`,
    { replace: true },
  );
  return <RouteLoading />;
};

const RedirectTryHaire = () => {
  const { interviewId } = useParams();
  const token = useSelector((state) => state.auth.token);
  const [interviewDetails, setInterviewDetails] = useState(null);

  useEffect(() => {
    (async function () {
      try {
        const data = await fetchTryInterviewDetails(token, interviewId);
        if (data?.status === 200) {
          setInterviewDetails(data);
        }
      } catch (error) {
        setInterviewDetails({ signal: false });
      }
    })();
  }, [interviewId]);

  return (
    <>
      {interviewDetails?.signal === true && (
        <TryInterview interviewStatusDetails={interviewDetails} />
      )}
      {interviewDetails?.signal === false && (
        <>
          <Dashboard />
        </>
      )}
    </>
  );
};
